import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages variant metafield rows visibility.
 * Shows only metafields for the currently selected variant.
 *
 * @extends Component
 */
export class ProductMetafieldsTable extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Listen for variant update events
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    // Initialize with current variant
    this.#initializeVariantRows();
    this.#hydrateMetaobjectJsonRoots();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) {
      // If no variant is selected, hide all variant metafield rows
      this.#hideAllVariantRows();
      return;
    }

    this.#showVariantRows(variantId);
  };

  /**
   * Initializes variant rows based on current variant selection
   */
  #initializeVariantRows() {
    // Check URL parameter first (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');

    if (variantIdFromUrl) {
      this.#showVariantRows(variantIdFromUrl);
      return;
    }

    // Check data attribute for initial variant ID
    const initialVariantId = this.dataset.initialVariantId;
    if (initialVariantId) {
      this.#showVariantRows(initialVariantId);
      return;
    }

    // Check if there's a product form with variant ID input
    const productForm = this.closest('product-form');
    if (productForm) {
      const variantIdInput = productForm.querySelector('[ref="variantId"]');
      if (variantIdInput && variantIdInput.value) {
        this.#showVariantRows(variantIdInput.value);
        return;
      }
    }

    // Fallback: find first available variant from the rows themselves
    const variantRows = this.querySelectorAll('.variant-metafield-row[data-variant-id]');
    if (variantRows.length > 0) {
      // Get unique variant IDs from rows
      const variantIds = new Set();
      variantRows.forEach(row => {
        const variantId = row.getAttribute('data-variant-id');
        if (variantId) {
          variantIds.add(variantId);
        }
      });

      // Show first variant's rows
      const firstVariantId = Array.from(variantIds)[0];
      if (firstVariantId) {
        this.#showVariantRows(firstVariantId);
        return;
      }
    }

    // If no variant found, hide all variant rows
    this.#hideAllVariantRows();
  }

  /**
   * Shows rows for a specific variant and hides all others
   * @param {string} variantId - The variant ID to show
   */
  #showVariantRows(variantId) {
    const allVariantRows = this.querySelectorAll('.variant-metafield-row[data-variant-id]');
    
    // Convert variantId to string for comparison
    const variantIdStr = String(variantId);
    
    allVariantRows.forEach(row => {
      const rowVariantId = String(row.getAttribute('data-variant-id'));
      if (rowVariantId === variantIdStr) {
        row.setAttribute('data-visible', 'true');
      } else {
        row.removeAttribute('data-visible');
      }
    });
  }

  /**
   * Hides all variant metafield rows
   */
  #hideAllVariantRows() {
    const allVariantRows = this.querySelectorAll('.variant-metafield-row[data-variant-id]');
    allVariantRows.forEach(row => {
      row.removeAttribute('data-visible');
    });
  }

  /**
   * Vult `.metaobject-json-root` cellen met gestructureerde output uit `| json` (geen Liquid string-split).
   */
  #hydrateMetaobjectJsonRoots() {
    this.querySelectorAll('.metaobject-json-root[data-metaobject-json]').forEach((root) => {
      const raw = root.getAttribute('data-metaobject-json');
      if (!raw) return;
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        root.removeAttribute('data-metaobject-json');
        return;
      }
      root.removeAttribute('data-metaobject-json');

      const entries =
        data !== null && typeof data === 'object' && !Array.isArray(data)
          ? Object.entries(data)
          : [['value', data]];

      for (const [fieldKey, rawVal] of entries) {
        if (fieldKey === 'type') continue;

        const row = document.createElement('div');
        row.className = 'metaobject-field';

        const label = document.createElement('strong');
        label.textContent =
          fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()) + ': ';
        row.appendChild(label);
        row.appendChild(this.#metaobjectValueToNode(rawVal));
        root.appendChild(row);
      }
    });
  }

  /**
   * @param {unknown} val
   * @returns {Node}
   */
  #metaobjectValueToNode(val) {
    if (val == null) {
      return document.createTextNode('');
    }
    if (typeof val === 'object') {
      const span = document.createElement('span');
      span.textContent = JSON.stringify(val);
      return span;
    }
    const s = String(val);
    if (s.includes('gid://shopify/')) {
      const span = document.createElement('span');
      span.className = 'gid-reference';
      span.textContent = s;
      return span;
    }
    if (s.startsWith('http://') || s.startsWith('https://')) {
      const a = document.createElement('a');
      a.href = s;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = s;
      return a;
    }
    return document.createTextNode(s);
  }
}

if (!customElements.get('product-metafields-table')) customElements.define('product-metafields-table', ProductMetafieldsTable);

